import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Grid, Form, Select, Header } from "semantic-ui-react";
import { getSystemsFromUserRoles, updateUserSettings } from "../../actions/users/users";
import { AppDispatch, RootState } from "../../reducers";
import { City, Region } from "../../types/regions";
import { exists } from "../../utils/utils";
import ButtonLink from "../common/ButtonLink";
import FormModal from "../common/FormModal";
import TableRow from "../common/TableRow";

const mapStateToProps = ({ users, userSettings, accounts, statusMessages }: RootState) => ({
  user: users.userClaims,
  defaultRegion: userSettings.defaultRegion as Region,
  defaultCity: userSettings.defaultCity as City,
  availableRegions: users.availableRegions,
  userSystems: getSystemsFromUserRoles(users.userClaims.roles ?? []),
  users: accounts.users,
  showSuccessMessage: statusMessages.showSuccessMessage,
  submitSucceededMessageHeader: statusMessages.submitSucceededMessageHeader,
  submitSucceededMessageContent: statusMessages.submitSucceededMessageContent,
  showFailureMessage: statusMessages.showFailureMessage,
  submitFailedMessageHeader: statusMessages.submitFailedMessageHeader,
  submitFailedMessageContent: statusMessages.submitFailedMessageContent,
  gettingUsers: accounts.gettingUsers
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateUserSettings: (options: {
    defaultRegionId?: number | null;
    defaultCityId?: number | null;
  }) => dispatch(updateUserSettings(options))
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type SettingsProps = {} & PropsFromRedux;
type SettingsState = { rolesPermissionsKey: number };

class Settings extends React.Component<SettingsProps, SettingsState> {
  constructor(props: SettingsProps) {
    super(props);
    this.state = { rolesPermissionsKey: 0 };
  }

  getRegionCities() {
    const region = this.props.availableRegions.find(({ id }) => id === this.props.defaultRegion.id);
    return region && region.cities ? region.cities : [];
  }

  compareEntitiesByName(a: City, b: City) {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  }

  renderDefaultCitySelector() {
    if (!this.props.userSystems.includes("penciler")) {
      return null;
    }
    const { defaultCity } = this.props;
    return (
      <TableRow
        title="Default City"
        value={exists(defaultCity) ? defaultCity.name : "None"}
        content={
          <FormModal
            fields={[
              {
                name: "defaultCityId",
                initialValue: exists(defaultCity) ? defaultCity.id : null,
                render: ({ input }) => (
                  <Form.Field
                    control={Select}
                    label={"Default city"}
                    placeholder="Select city"
                    name={input.name}
                    value={input.value}
                    onBlur={input.onBlur}
                    onFocus={input.onFocus}
                    onChange={(_: any, { value }: any) => input.onChange(value)}
                    options={this.getRegionCities()
                      .slice()
                      .sort(this.compareEntitiesByName)
                      .map(({ id, name }) => ({
                        key: id,
                        text: name,
                        value: id
                      }))}
                  />
                )
              }
            ]}
            onSubmit={({ defaultCityId }) => this.props.updateUserSettings({ defaultCityId })}
            submitText={"Save"}
            modalHeader={"Change default city"}
            modalTrigger={onClick => <ButtonLink onClick={onClick}>Change</ButtonLink>}
          />
        }
      />
    );
  }

  renderDefaultRegionSelector() {
    const { defaultRegion, availableRegions } = this.props;
    const regionName =
      exists(defaultRegion) && exists(defaultRegion.name) ? defaultRegion.name : "None";
    return (
      <TableRow
        title="Default Region"
        value={regionName}
        content={
          <FormModal
            fields={[
              {
                name: "defaultRegionId",
                initialValue: exists(defaultRegion) ? defaultRegion.id : null,
                render: ({ input }) => (
                  <Form.Field
                    aria-label="Default region selection"
                    control={Select}
                    label={"Default region"}
                    placeholder="Select region"
                    name={input.name}
                    value={input.value}
                    onBlur={input.onBlur}
                    onFocus={input.onFocus}
                    onChange={(_: any, { value }: any) => input.onChange(value)}
                    options={availableRegions.map(({ id, name }) => ({
                      key: id,
                      text: name,
                      value: id
                    }))}
                  />
                )
              }
            ]}
            onSubmit={({ defaultRegionId }) =>
              this.props.updateUserSettings({ defaultRegionId, defaultCityId: null })
            }
            submitText={"Save"}
            modalHeader={"Change default region"}
            modalTrigger={onClick => <ButtonLink onClick={onClick}>Change</ButtonLink>}
          />
        }
      />
    );
  }

  render() {
    return (
      <Grid textAlign="center" style={{ height: "100%" }} verticalAlign="top">
        <Grid.Row columns={1} style={{ maxWidth: 750, marginTop: "20px" }}>
          <Grid.Column width={16} textAlign="left">
            <Header as="h2" textAlign="left">
              Viewport Settings
            </Header>
          </Grid.Column>
        </Grid.Row>
        {this.renderDefaultRegionSelector()}
        {this.renderDefaultCitySelector()}
      </Grid>
    );
  }
}

export default connector(Settings);
